
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import App from './App';
import './index.css';
import { DarkroomModeProvider } from './hooks/useDarkroomMode';
import { Toaster } from "@/components/ui/toaster";
import { Capacitor } from '@capacitor/core';

// Initialize language from localStorage or browser language
(function initializeLanguage() {
  try {
    const storedLanguage = localStorage.getItem('appLanguage');
    const browserLang = navigator.language.split('-')[0];
    const supportedLanguages = ['en', 'es', 'fr', 'de', 'zh', 'ja', 'ko', 'pt'];
    
    const language = storedLanguage || 
      (supportedLanguages.includes(browserLang) ? browserLang : 'en');
    
    document.documentElement.lang = language;
    
    if (!storedLanguage) {
      localStorage.setItem('appLanguage', language);
    }
    
    console.log('Language initialized to:', language);
  } catch (error) {
    console.error('Error initializing language:', error);
    document.documentElement.lang = 'en';
  }
})();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Detect if running in Capacitor (native app)
const isCapacitorApp = Capacitor.isNativePlatform();
console.log('Running in Capacitor environment:', isCapacitorApp);

// iOS WebView detection
const isIOS = isCapacitorApp && Capacitor.getPlatform() === 'ios' ||
              /iPad|iPhone|iPod/.test(navigator.userAgent) || 
              (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

if (isIOS) {
  console.log('iOS device detected, applying WebView optimizations');
  
  // Force-fix viewport settings for iOS
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (viewportMeta) {
    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=no, maximum-scale=1.0');
  }

  // Ensure body is visible and properly sized
  document.body.style.opacity = '1';
  document.body.style.height = '100%';
  document.body.style.width = '100%';
  document.body.style.overflow = 'auto';
  
  // Fix for TypeScript error - using setAttribute instead of direct property assignment
  document.body.setAttribute('style', document.body.getAttribute('style') + '; -webkit-overflow-scrolling: touch;');
  
  // Add additional debug info to check what's happening
  console.log('Document ready state:', document.readyState);
  console.log('Document body exists:', !!document.body);
  console.log('Root element exists:', !!document.getElementById('root'));
}

// Improved robust rendering approach
const renderApp = () => {
  console.log('Rendering app...');
  const rootElement = document.getElementById('root');
  
  if (!rootElement) {
    console.error('Root element not found!');
    return;
  }
  
  console.log('Root element found, creating React root');
  
  try {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            {/* Important: Set enableSystem to true and defaultTheme to system to ensure proper initialization */}
            <ThemeProvider attribute="class" enableSystem={true} defaultTheme="system">
              <DarkroomModeProvider>
                <App />
                <Toaster />
              </DarkroomModeProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </React.StrictMode>
    );
    console.log('App successfully rendered');
  } catch (error) {
    console.error('Error rendering app:', error);
  }
};

// Enhanced rendering strategy for better iOS WebView compatibility
if (isCapacitorApp) {
  console.log('Capacitor environment detected - using optimized rendering');
  
  // For native environments, especially iOS, we need more robust initialization
  document.addEventListener('DOMContentLoaded', () => {
    console.log('DOM content loaded in Capacitor app');
    
    // Small delay for iOS WebView to be fully ready
    setTimeout(() => {
      console.log('Rendering after delay in Capacitor environment');
      renderApp();
      
      // Force a repaint after rendering
      setTimeout(() => {
        document.body.style.display = 'none';
        setTimeout(() => document.body.style.display = 'block', 10);
      }, 300);
    }, 300);
  });
  
  // Backup render in case DOMContentLoaded already fired
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    console.log('Document already ready, rendering with delay');
    setTimeout(renderApp, 300);
  }
} else {
  console.log('Browser environment detected - using standard rendering');
  renderApp();
}
